import { Outlet, NavLink, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Dumbbell, 
  FileText, 
  Calendar, 
  Users, 
  LogOut,
  Menu,
  X,
  Utensils,
  Sparkles,
  Settings
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { useState } from "react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ycyh3qv2_goatgym_logo.png.jpeg";

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { path: "/admin/clients", icon: Users, label: "Clientes" },
    { path: "/admin/premium-crm", icon: Sparkles, label: "Premium CRM", color: "text-[#D4AF37]" },
    { path: "/admin/exercise-library", icon: Dumbbell, label: "Biblioteca Ejercicios", color: "text-green-400" },
    { path: "/admin/programs", icon: FileText, label: "Programas" },
    { path: "/admin/diets", icon: Utensils, label: "Dietas" },
    { path: "/admin/recipes", icon: Utensils, label: "Recetas" },
    { path: "/admin/app-config", icon: Settings, label: "Control Módulos", color: "text-purple-400" },
  ];

  const isActive = (path, end) => {
    if (end) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-dvh bg-[#0B0B0B] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0B0B0B] border-r border-[#1A1A1A] transform transition-transform duration-300 lg:transform-none pt-safe",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 px-4 flex items-center justify-between border-b border-[#1A1A1A]">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="GOATGYM" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="font-heading font-black text-[#D4AF37] leading-tight tracking-tight">GOATGYM</h1>
              <p className="text-xs text-[#555]">Panel Admin</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-3 rounded-lg hover:bg-[#1A1A1A] min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5 text-[#666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const active = isActive(item.path, item.end);
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200",
                  active
                    ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                    : "text-[#666] hover:text-white hover:bg-[#1A1A1A]"
                )}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User info & logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#1A1A1A]">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.email}</p>
              <p className="text-xs text-[#555]">Administrador</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-[#555] hover:text-[#D4AF37] hover:bg-[#1A1A1A]"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 px-4 flex items-center justify-between bg-[#0B0B0B]/95 backdrop-blur-md border-b border-[#1A1A1A] pt-safe">
          <div className="h-14 flex items-center justify-between w-full">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-3 rounded-lg hover:bg-[#1A1A1A] min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="GOATGYM" 
                className="w-8 h-8 object-contain"
              />
              <span className="font-heading font-black text-[#D4AF37]">GOATGYM</span>
            </div>
            <div className="w-11" /> {/* Spacer */}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto pb-safe">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
